/**
 * TimesheetPeriods.jsx — Puantaj Dönem Yönetimi
 * (Eski TimesheetOperationsPanel'in yeniden yazılmış hali)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Calculator, Download, LockKeyhole, RefreshCw, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { timesheetApi } from '../../../api/axios';
import { getTimesheetStatusBadgeClass } from '../utils/attendanceHelpers';

const statusLabels = (status, t) => ({
  DRAFT: t('timesheet_status_draft', 'Taslak'),
  CALCULATED: t('timesheet_status_calculated', 'Hesaplandı'),
  PENDING_APPROVAL: t('timesheet_status_pending', 'Onay bekliyor'),
  APPROVED: t('timesheet_status_approved', 'Onaylandı'),
  LOCKED: t('timesheet_status_locked', 'Kilitli'),
  EXPORTED: t('timesheet_status_exported', 'Çıktı alındı'),
}[status] || status);

const TimesheetPeriods = () => {
  const { t } = useTranslation();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ period_start: '', period_end: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timesheetApi.listPeriods();
      setPeriods(res.data || []);
    } catch {
      toast.error(t('err_timesheet_load', 'Puantaj dönemleri yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await timesheetApi.createPeriod(form);
      setForm({ period_start: '', period_end: '' });
      toast.success(t('msg_timesheet_created', 'Puantaj dönemi oluşturuldu.'));
      await load();
    } catch {
      toast.error(t('err_timesheet_create', 'Puantaj dönemi oluşturulamadı.'));
    }
  };

  const action = async (promise, message) => {
    try {
      await promise;
      toast.success(message);
      await load();
    } catch {
      toast.error(t('err_timesheet_action', 'Puantaj işlemi başarısız.'));
    }
  };

  const downloadCsv = async (period) => {
    try {
      const res = await timesheetApi.exportPeriod(period.id);
      if (res?.data?.csv) {
        const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheet-${period.period_start}-${period.period_end}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error(t('err_timesheet_export', 'Dönem çıktısı alınamadı.'));
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">
            {t('timesheet_kicker', 'Puantaj Operasyonu')}
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-900">
            {t('timesheet_title', 'Puantaj Dönem Hesaplama ve Çıktı')}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {t('timesheet_desc', 'Hesapla, onaya gönder, kilitle ve muhasebe çıktısını al.')}
          </p>
        </div>
        <button onClick={load} className="rounded-xl bg-slate-100 p-2 text-slate-500 hover:bg-slate-200 transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Yeni dönem formu */}
      <form onSubmit={create} className="flex flex-wrap items-end gap-3 rounded-2xl bg-slate-50 p-4 mb-5">
        <label className="text-xs font-bold text-slate-600">
          {t('timesheet_start', 'Başlangıç')}
          <input
            required
            type="date"
            value={form.period_start}
            onChange={(e) => setForm((p) => ({ ...p, period_start: e.target.value }))}
            className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs font-bold text-slate-600">
          {t('timesheet_end', 'Bitiş')}
          <input
            required
            type="date"
            value={form.period_end}
            onChange={(e) => setForm((p) => ({ ...p, period_end: e.target.value }))}
            className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </label>
        <button className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 transition-colors">
          <Plus size={14} />
          {t('timesheet_create', 'Oluştur')}
        </button>
      </form>

      {/* Dönem listesi */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      ) : periods.length === 0 ? (
        <p className="text-sm text-slate-400">{t('timesheet_empty', 'Henüz puantaj dönemi yok.')}</p>
      ) : (
        <div className="space-y-3">
          {periods.map((period) => (
            <div key={period.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 p-4">
              <div>
                <div className="font-bold text-slate-900 text-sm">
                  {period.period_start} → {period.period_end}
                </div>
                <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide ${getTimesheetStatusBadgeClass(period.status)}">
                  {t('timesheet_status', 'Durum')}: {statusLabels(period.status, t)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {period.status === 'DRAFT' && (
                  <button
                    onClick={() => action(timesheetApi.calculatePeriod(period.id), t('timesheet_calculated', 'Puantaj hesaplandı.'))}
                    className="flex items-center gap-1 rounded-lg bg-cyan-600 px-3 py-2 text-[10px] font-bold text-white hover:bg-cyan-700"
                  >
                    <Calculator size={12} /> {t('timesheet_calculate', 'Hesapla')}
                  </button>
                )}
                {period.status === 'CALCULATED' && (
                  <button
                    onClick={() => action(timesheetApi.changeStatus(period.id, 'PENDING_APPROVAL'), t('timesheet_sent_approval', 'Onaya gönderildi.'))}
                    className="rounded-lg bg-amber-500 px-3 py-2 text-[10px] font-bold text-white hover:bg-amber-600"
                  >
                    {t('timesheet_send_approval', 'Onaya gönder')}
                  </button>
                )}
                {period.status === 'PENDING_APPROVAL' && (
                  <button
                    onClick={() => action(timesheetApi.changeStatus(period.id, 'APPROVED'), t('timesheet_approved', 'Puantaj onaylandı.'))}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-[10px] font-bold text-white hover:bg-emerald-700"
                  >
                    {t('timesheet_approve', 'Onayla')}
                  </button>
                )}
                {period.status === 'APPROVED' && (
                  <button
                    onClick={() => action(timesheetApi.changeStatus(period.id, 'LOCKED'), t('timesheet_locked', 'Puantaj kilitlendi.'))}
                    className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-[10px] font-bold text-white hover:bg-indigo-700"
                  >
                    <LockKeyhole size={12} /> {t('timesheet_lock', 'Kilitle')}
                  </button>
                )}
                {period.status === 'LOCKED' && (
                  <button
                    onClick={() => downloadCsv(period)}
                    className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-bold text-white hover:bg-slate-800"
                  >
                    <Download size={12} /> {t('timesheet_export', 'Çıktı al')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimesheetPeriods;